import type { TFunction } from "i18next";
import type { ChatScopeFilters } from "../api";
import type { FilterOption } from "../components/FilterMultiSelect";
import type { WatchlistScope } from "../watchlist/types";
import type { AskSuggestion } from "./askStarters";

/** A custom chat uses a rolling window; a page context retains its exact filters. */
export function filtersForScope(
  scope: WatchlistScope,
  options: FilterOption[],
  anchor: number,
): ChatScopeFilters {
  const from = new Date(anchor);
  from.setHours(0, 0, 0, 0);
  from.setDate(from.getDate() - (scope.window.days - 1));
  return {
    userIds: scope.userIds,
    from: from.toISOString(),
    to: new Date(anchor).toISOString(),
    processes: scope.processSlugs.length
      ? scope.processSlugs.map(slug => options.find(option => option.id === slug)?.label ?? slug)
      : undefined,
  };
}

/** Seed an editable recurring scope. Never silently broaden a missing process or a single session. */
export function editableScope(
  filters: ChatScopeFilters | undefined,
  users: FilterOption[],
  processes: FilterOption[],
): WatchlistScope {
  const names = filters?.processes ?? [];
  // Preserve finite calendar windows. The epoch sentinel represents All time,
  // for which a recurring KPI starts with an explicit 30-day rolling window.
  let days = 30;
  if (filters?.from && filters.to) {
    const from = new Date(filters.from);
    const to = new Date(filters.to);
    if (
      Number.isFinite(from.getTime()) &&
      Number.isFinite(to.getTime()) &&
      from.getTime() !== 0 &&
      from < to
    ) {
      const dateValue = (date: Date) =>
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
      const includesLastDay =
        to.getHours() || to.getMinutes() || to.getSeconds() || to.getMilliseconds();
      days = Math.max(
        1,
        (dateValue(to) - dateValue(from)) / 86_400_000 + (includesLastDay ? 1 : 0),
      );
    }
  }
  return {
    userIds: filters?.recordingIds ? [] : (filters?.userIds ?? users.map(user => user.id)),
    processSlugs: processes
      .filter(process => names.includes(process.label))
      .map(process => process.id),
    allProcesses: names.length === 0,
    window: { days },
  };
}

/** The starter questions a page context offers when it has none of its own. */
export const defaultAskSuggestions = (t: TFunction): AskSuggestion[] => [
  { label: t("watchlist.suggestion_apps"), icon: "apps" },
  { label: t("watchlist.suggestion_rework"), icon: "rework" },
  { label: t("watchlist.suggestion_automation"), icon: "automation" },
];

/** Date controls include the final day; the chat endpoint takes an exclusive upper bound. */
export function pageDateFilters(from = "", to = ""): Pick<ChatScopeFilters, "from" | "to"> | null {
  const start = from ? new Date(`${from}T00:00:00`) : new Date(0);
  const end = to ? new Date(`${to}T00:00:00`) : new Date();
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() + 1);
  return Number.isFinite(start.getTime()) && Number.isFinite(end.getTime()) && start < end
    ? { from: start.toISOString(), to: end.toISOString() }
    : null;
}
